import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Table } from '@opendate-app/setlist';

export const formatCents = (cents, currency) => (
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((cents || 0) / 100)
);

const STATE_BADGES = {
  unused: { color: 'success', label: 'Unused' },
  used: { color: 'brand', label: 'Used' },
  refund_pending: { color: 'warning', label: 'Refund pending' },
  refund_failed: { color: 'error', label: 'Refund failed' },
  refunded: { color: 'gray', label: 'Purchase refunded' },
  voided: { color: 'gray', label: 'Voided' },
  disputed: { color: 'error', label: 'Charge disputed' },
};

const SPEND_RETURN_LABELS = {
  pending: 'refund pending',
  failed: 'refund failed',
  refunded: 'refunded',
  held: 'held until the dispute is decided',
  resolved: 'dispute decided',
};

export const canActOn = (giftCard) => (
  (giftCard.canVoid && !giftCard.voidRefusal) || (giftCard.canRefundPurchase && !giftCard.refundRefusal)
);

const OrderGiftCardsCard = ({ giftCards, onVoidOrRefund }) => {
  if (giftCards.length === 0) return null;

  const columns = [
    { key: 'card', header: 'Card', render: (giftCard) => `•••• ${giftCard.last4}` },
    { key: 'recipient', header: 'Recipient', render: (giftCard) => giftCard.recipientName || '—' },
    { key: 'value', header: 'Value', align: 'right', render: (giftCard) => formatCents(giftCard.initialValueCents, giftCard.currency) },
    { key: 'balance', header: 'Balance', align: 'right', render: (giftCard) => formatCents(giftCard.balanceCents, giftCard.currency) },
    {
      key: 'state',
      header: 'Status',
      render: (giftCard) => {
        const badge = STATE_BADGES[giftCard.state] || STATE_BADGES.used;
        return (
          <>
            <Badge color={badge.color} size="sm">{badge.label}</Badge>
            {(giftCard.spendReturns || []).map((spendReturn) => (
              <div key={spendReturn.id} className="op-muted" data-testid="spend-return">
                {`${formatCents(spendReturn.amountCents, giftCard.currency)} ${spendReturn.kind === 'returned_fees' ? 'purchase fees' : 'spent on tickets'} returned to the buyer · ${SPEND_RETURN_LABELS[spendReturn.state] || spendReturn.state}`}
              </div>
            ))}
          </>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (giftCard) => (canActOn(giftCard) ? (
        <Button size="sm" variant="secondary" onClick={() => onVoidOrRefund(giftCard)}>
          Void or refund
        </Button>
      ) : null),
    },
  ];

  return (
    <div className="op-card">
      <h2 className="op-section-title">Gift Cards</h2>
      <Table columns={columns} data={giftCards} getRowKey={(giftCard) => giftCard.id} />
    </div>
  );
};

export const giftCardShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  last4: PropTypes.string.isRequired,
  status: PropTypes.string,
  currency: PropTypes.string,
  state: PropTypes.oneOf(Object.keys(STATE_BADGES)).isRequired,
  initialValueCents: PropTypes.number,
  balanceCents: PropTypes.number,
  bonusValueCents: PropTypes.number,
  spentCents: PropTypes.number,
  unused: PropTypes.bool,
  recipientName: PropTypes.string,
  refundableCents: PropTypes.number,
  refundedCents: PropTypes.number,
  spendReturns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    kind: PropTypes.string,
    amountCents: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
  })),
  voidRefusal: PropTypes.string,
  refundRefusal: PropTypes.string,
  canVoid: PropTypes.bool,
  canRefundPurchase: PropTypes.bool,
  voidUrl: PropTypes.string.isRequired,
  purchaseRefundUrl: PropTypes.string.isRequired,
});

OrderGiftCardsCard.propTypes = {
  giftCards: PropTypes.arrayOf(giftCardShape),
  onVoidOrRefund: PropTypes.func.isRequired,
};

OrderGiftCardsCard.defaultProps = {
  giftCards: [],
};

export default OrderGiftCardsCard;
